import type { Participant } from '@seen/core/models/patients/participant'
import { cn } from '@seen/ui'
import {
  type Editor,
  EditorContent,
  type JSONContent,
  type UseEditorOptions,
  useEditor,
} from '@tiptap/react'
import { cva } from 'class-variance-authority'
import {
  type JSX,
  type MouseEventHandler,
  type MutableRefObject,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { tiptapVariants } from '#src/components/core/tiptapVariants.ts'
import { EncounterMedicationsBlockNode } from '#src/components/rich-text-editor/EncounterMedicationsBlockNode/EncounterMedicationsBlockNode.tsx'
import { getRenderExtensions } from '#src/components/rich-text-editor/extensions.ts'
import { ConfigContext } from '#src/contexts/config/ConfigContext.tsx'
import { useSyncEditorContent } from '#src/hooks/useSyncEditorContent.ts'
import { stripEmptyEdgeParagraphs } from '#src/utils/html.ts'

export interface RichTextProps {
  value: string | JSONContent
  inline?: boolean
  // Leave unset for the host's `immediatelyRenderForSSR`.
  immediatelyRender?: boolean
  className?: string
  editorRef?: MutableRefObject<Editor | null>
  onClick?: MouseEventHandler<HTMLDivElement>
  /* Participant in scope at the render site. Mirrors `RichTextEditor.participant`
   * / `CollaborativeEditor.participant`. Threads through to participant-aware
   * nodes (today: `encounter-medications-block`) so a read-only render of a
   * visit-note body inside a participant page shows encounter-linked
   * medications. Omit for generic renders (comments, observations, faxes,
   * templates without a participant in scope) — content containing the
   * encounter-medications-block then renders its "No patient selected" empty
   * state, matching the editor behavior. */
  participant?: Participant
}

export function RichText({
  value,
  inline,
  className,
  editorRef,
  onClick,
  participant,
  immediatelyRender,
}: RichTextProps): JSX.Element {
  const config = useContext(ConfigContext)
  const _immediately_render =
    immediatelyRender ?? config?.immediatelyRenderForSSR
  const extensions = useMemo(() => {
    const base = getRenderExtensions()
    const patientReferenceString = participant?.reference.reference
    if (patientReferenceString === undefined) {
      return base
    }
    /* Only the encounter-medications-block needs per-render configuration
     * today. Mirrors `configureForEditor` in `useTiptapExtensions` for the
     * same node — keep them in sync if more participant-aware render config
     * is added. */
    return base.map((ext) =>
      ext.name === 'encounter-medications-block'
        ? EncounterMedicationsBlockNode.configure({ patientReferenceString })
        : ext
    )
  }, [participant])
  // TODO: styling
  const options = useMemo(
    () =>
      ({
        extensions,
        content:
          typeof value === 'string' ? stripEmptyEdgeParagraphs(value) : value,
        parseOptions: {
          // ##PreserveWhitespace Prevents spaces between adjacent inline nodes from collapsing.
          preserveWhitespace: true,
        },
        editable: false,
        immediatelyRender: _immediately_render,
        editorProps: {
          attributes: {
            class: cn(
              tiptapVariants({ noPlaceholder: true }),
              // Hard-break notes otherwise keep a cursor-only trailing <br>
              // that reads as extra padding under the last line.
              '[&_br.ProseMirror-trailingBreak]:hidden',
              variants({ inline, className })
            ),
          },
        },
        // enableContentCheck: true,
        // onContentError: handleTiptapContentError,
      }) satisfies UseEditorOptions,
    [value, inline, className, extensions, _immediately_render]
  )
  const editor = useEditor(options)
  // A host outside Portal (portalOrigin) opens root-relative links stored in
  // the content on Portal, wherever this renders (body-portaled drawers too);
  // plain and middle clicks both, like the shared Link.
  const portalOrigin = config?.portalOrigin
  const openOnPortal = useCallback<MouseEventHandler<HTMLDivElement>>(
    (event) => {
      if (!portalOrigin || event.button > 1) {
        return
      }
      const link =
        event.target instanceof Element ? event.target.closest('a[href]') : null
      const href = link?.getAttribute('href') ?? ''
      if (!href.startsWith('/') || href.startsWith('//')) {
        return
      }
      event.preventDefault()
      window.open(new URL(href, portalOrigin).href, '_blank', 'noopener')
    },
    [portalOrigin]
  )

  if (editorRef) {
    editorRef.current = editor
  }

  useSyncEditorContent(
    editor,
    typeof value === 'string' ? stripEmptyEdgeParagraphs(value) : value
  )

  return (
    <EditorContent
      className={cn(inline && 'inline')}
      editor={editor}
      onAuxClick={openOnPortal}
      onClick={onClick}
      onClickCapture={openOnPortal}
    />
  )
}

const variants = cva('', {
  variants: {
    inline: {
      true: 'inline [&_p]:inline',
      false: '',
    },
  },
  defaultVariants: {
    inline: false,
  },
})
